import React, { useId } from 'react';
import { Controller } from 'react-hook-form';
import Field from './Field';

const DEFAULT_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' }
];

/**
 * Segmented Yes/No (or custom option) control for react-hook-form.
 *   <YesNoToggle name="accidents.hadAny" control={control} label="…" rules={{ validate: v => v !== null || 'Required' }} />
 * Value is boolean by default; pass `options` for string values (e.g. yes/no/na).
 */
const YesNoToggle = ({
  name,
  control,
  label,
  hint,
  required,
  rules,
  options = DEFAULT_OPTIONS,
  className = '',
  disabled = false,
  size = 'md'
}) => {
  const generatedId = useId();
  const id = `${name}-${generatedId}`;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules || (required ? { validate: (value) => (value !== null && value !== undefined && value !== '') || 'Please choose an answer' } : undefined)}
      render={({ field, fieldState }) => (
        <Field id={id} label={label} hint={hint} error={fieldState.error?.message} required={required} className={className} labelAs="div">
          <div
            role="radiogroup"
            data-field={name}
            tabIndex={-1}
            aria-labelledby={label ? `${id}-label` : undefined}
            aria-describedby={fieldState.error ? `${id}-error` : undefined}
            className="inline-flex flex-wrap gap-2"
          >
            {options.map((option, index) => {
              const checked = field.value === option.value;
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  disabled={disabled}
                  ref={index === 0 ? field.ref : undefined}
                  onClick={() => field.onChange(option.value)}
                  onBlur={field.onBlur}
                  className={[
                    'min-w-[72px] rounded-md border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500',
                    size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2.5 text-base sm:text-sm',
                    checked
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:bg-blue-50',
                    fieldState.error && !checked ? 'border-red-400' : '',
                    disabled ? 'opacity-60 cursor-not-allowed' : ''
                  ].join(' ')}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </Field>
      )}
    />
  );
};

export default YesNoToggle;
